import React, { useState, useCallback, memo, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Fab,
  InputBase,
  alpha,
  Slide,
  Fade,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ChildCare as ChildCareIcon,
  Event as EventIcon,
  CalendarMonth as CalendarIcon,
  Sports as SportsIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import UserAvatar from './UserAvatar';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import Footer from './Footer';
import Logo from '../Logo';
import { useLanguage } from '../../contexts/LanguageContext';

const drawerWidth = 240;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout, isAdmin, user, userProfile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Phase 2 UX Revolution states
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bottomNavValue, setBottomNavValue] = useState('/dashboard');

  // Close menu and drawer when location changes
  useEffect(() => {
    setAnchorEl(null);
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Sync bottom navigation with current route
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) setBottomNavValue('/dashboard');
    else if (path.startsWith('/children')) setBottomNavValue('/children');
    else if (path.startsWith('/activities')) setBottomNavValue('/activities');
    else if (path.startsWith('/calendar')) setBottomNavValue('/calendar');
    else if (path.startsWith('/chores')) setBottomNavValue('/chores');
    else if (path.startsWith('/tasks')) setBottomNavValue('/tasks');
    else if (path.startsWith('/grocery')) setBottomNavValue('/grocery');
    else if (path.startsWith('/co-parent-management')) setBottomNavValue('/co-parent-management');
  }, [location.pathname]);

  // Search handlers
  const handleSearchOpen = useCallback(() => {
    setSearchOpen(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to activities page with search query
      navigate(`/activities?search=${encodeURIComponent(searchQuery.trim())}`);
      handleSearchClose();
    }
  }, [searchQuery, navigate, handleSearchClose]);

  // Quick add handler
  const handleQuickAdd = useCallback(() => {
    // Navigate to activities page with add dialog open
    navigate('/activities?action=add');
  }, [navigate]);

  // Bottom navigation handler
  const handleBottomNavChange = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setBottomNavValue(newValue);
    navigate(newValue);
  }, [navigate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to open search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      // ESC to close search
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
      // Ctrl+N or Cmd+N to quick add (when not in input)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !searchOpen) {
        const activeElement = document.activeElement;
        if (activeElement?.tagName !== 'INPUT' && activeElement?.tagName !== 'TEXTAREA') {
          e.preventDefault();
          handleQuickAdd();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen, handleQuickAdd]);

  const handleDrawerToggle = useCallback(() => {
    // Always close menu when toggling drawer
    setAnchorEl(null);
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  }, [logout, navigate]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newPath: string) => {
    console.log('Navigating to path:', newPath);
    // Close menu and drawer before navigation
    setAnchorEl(null);
    setMobileOpen(false);
    navigate(newPath);
  }, [navigate]);

  // Get current tab value from location
  const getCurrentTab = () => {
    const path = location.pathname;
    console.log('Current path:', path);
    if (path.startsWith('/dashboard')) return '/dashboard';
    if (path.startsWith('/children')) return '/children';
    if (path.startsWith('/activities')) return '/activities';
    if (path.startsWith('/calendar')) return '/calendar';
    if (path.startsWith('/co-parent-management')) return '/co-parent-management';
    if (path.startsWith('/profile')) return '/profile';
    if (path.startsWith('/settings')) return '/settings';
    if (path.startsWith('/about')) return '/about';
    if (path.startsWith('/admin-tools')) return '/admin-tools';
    if (path.startsWith('/admin-panel')) return '/admin-panel';
    if (path.startsWith('/admin-portal')) return '/admin-portal';
    return false;
  };

  const handleNavClick = useCallback((path: string) => {
    console.log('Clicked navigation item:', path);
    // Immediately close drawer and menu
    setMobileOpen(false);
    setAnchorEl(null);
    // Small delay to ensure drawer closes before navigation
    setTimeout(() => {
      navigate(path);
    }, 50);
  }, [navigate]);

  const handleOpenUserMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Close drawer when opening user menu
    setMobileOpen(false);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseUserMenu = useCallback(() => {
    // Force close menu and clear all states
    setAnchorEl(null);
    setMobileOpen(false);
  }, []);

  const handleProfileClick = useCallback(() => {
    // Force close menu immediately
    setAnchorEl(null);
    setMobileOpen(false);
    // Add delay to ensure menu closes before navigation
    setTimeout(() => {
      navigate('/profile');
    }, 100);
  }, [navigate]);

  const handleSettingsClick = useCallback(() => {
    // Force close menu immediately
    setAnchorEl(null);
    setMobileOpen(false);
    // Add delay to ensure menu closes before navigation
    setTimeout(() => {
      navigate('/settings');
    }, 100);
  }, [navigate]);

  const handleAboutClick = useCallback(() => {
    // Force close menu immediately
    setAnchorEl(null);
    setMobileOpen(false);
    // Add delay to ensure menu closes before navigation
    setTimeout(() => {
      navigate('/about');
    }, 100);
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    // Force close menu immediately
    setAnchorEl(null);
    setMobileOpen(false);
    // Add small delay before logout
    setTimeout(() => {
      handleLogout();
    }, 100);
  }, [handleLogout]);

  const navigationItems = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('nav.children'), icon: <ChildCareIcon />, path: '/children' },
    { text: t('nav.activities'), icon: <SportsIcon />, path: '/activities' },
    { text: t('nav.calendar'), icon: <CalendarIcon />, path: '/calendar' },
    { text: t('nav.chores'), icon: <AssignmentIcon />, path: '/chores' },
    { text: t('nav.tasks'), icon: <CheckCircleIcon />, path: '/tasks' },
    { text: t('nav.grocery'), icon: <ShoppingCartIcon />, path: '/grocery' },
    { text: t('nav.coparents'), icon: <PersonIcon />, path: '/co-parent-management' },
    { text: t('nav.settings'), icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ backgroundColor: theme.palette.primary.main, color: 'white' }}>
        <Logo variant="small" color="white" />
      </Toolbar>
      <Divider />
      <Box sx={{ backgroundColor: theme.palette.background.default, height: '100%' }}>
        <List>
          {navigationItems.map((item) => (
            <ListItem disablePadding key={item.path}>
              <ListItemButton 
                onClick={() => handleNavClick(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    color: location.pathname === item.path ? 'primary' : 'inherit',
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`, 
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: 'none',
          // Remove any potential backdrop filters on mobile
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none'
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton onClick={handleDrawerToggle} edge="start" sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <Logo variant="large" showText={true} />
            </RouterLink>
          </Box>
          
          {/* Spacer to push navigation tabs to right */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Main navigation tabs - align to right */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Tabs value={getCurrentTab()} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
              <Tab icon={<DashboardIcon />} label={t('nav.dashboard')} value="/dashboard" />
              <Tab icon={<ChildCareIcon />} label={t('nav.children')} value="/children" />
              <Tab icon={<SportsIcon />} label={t('nav.activities')} value="/activities" />
              <Tab icon={<CalendarIcon />} label={t('nav.calendar')} value="/calendar" />
              <Tab icon={<AssignmentIcon />} label={t('nav.chores')} value="/chores" />
              <Tab icon={<CheckCircleIcon />} label={t('nav.tasks')} value="/tasks" />
              <Tab icon={<ShoppingCartIcon />} label={t('nav.grocery')} value="/grocery" />
              <Tab icon={<PersonIcon />} label={t('nav.coparents')} value="/co-parent-management" />
            </Tabs>
          </Box>
          
          {/* User profile and settings section */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            {/* Global Search Button */}
            <Tooltip title="Search activities (Ctrl+K)">
              <IconButton
                onClick={handleSearchOpen}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Toggle Dark/Light Mode">
              <ThemeToggle />
            </Tooltip>

            <LanguageToggle />

            <Tooltip title="Account settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 1 }}>
                <Avatar alt={user?.displayName || 'User'} src={userProfile?.photoURL || user?.photoURL || ''}>
                  {!(userProfile?.photoURL || user?.photoURL) && (user?.displayName?.[0] || user?.email?.[0].toUpperCase() || 'U')}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
          
          <Menu
            sx={{ mt: '45px' }}
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleCloseUserMenu}
            transitionDuration={200}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              },
            }}
            BackdropProps={{
              sx: {
                backgroundColor: 'transparent',
              },
              onClick: handleCloseUserMenu,
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <Typography textAlign="center">{t('nav.profile')}</Typography>
            </MenuItem>
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <Typography textAlign="center">{t('nav.settings')}</Typography>
            </MenuItem>
            <MenuItem onClick={handleAboutClick}>
              <ListItemIcon>
                <InfoIcon fontSize="small" />
              </ListItemIcon>
              <Typography textAlign="center">{t('nav.about')}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography textAlign="center">{t('nav.logout')}</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Mobile drawer */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          display: { md: 'none' } 
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
            // Properly clear backdrop on close
            BackdropProps: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
            // Force backdrop to unmount when drawer closes
            onBackdropClick: handleDrawerToggle,
          }}
          transitionDuration={300}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Only show permanent drawer on tablet, not on desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)`, md: '100%' },
          ml: { sm: `${drawerWidth}px`, md: 0 },
          mt: { xs: '56px', sm: '64px' },
          backgroundColor: theme.palette.background.default,
          minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          // Mobile-specific optimizations
          ...(isSmallScreen && {
            p: 1,
            mt: '56px',
            width: '100%',
            ml: 0,
            pb: '80px', // Extra padding for bottom navigation + FAB
          })
        }}
      >
        {/* Removed redundant toolbar spacer */}
        {children}
      </Box>
      <Footer />

      {/* Global Search Overlay */}
      <Fade in={searchOpen}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(theme.palette.background.default, 0.95),
            zIndex: theme.zIndex.modal + 1,
            display: searchOpen ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 8, sm: 10 },
          }}
          onClick={handleSearchClose}
        >
          <Paper
            component="form"
            onSubmit={handleSearchSubmit}
            onClick={(e) => e.stopPropagation()}
            sx={{
              p: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              width: { xs: '90%', sm: '600px', md: '700px' },
              borderRadius: '16px',
              boxShadow: theme.shadows[8],
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <SearchIcon sx={{ color: theme.palette.text.secondary, ml: 1 }} />
            <InputBase
              sx={{
                ml: 2,
                flex: 1,
                fontSize: { xs: '1rem', sm: '1.2rem' },
                '& input': {
                  padding: '12px 0',
                }
              }}
              placeholder="Search activities... (e.g., 'soccer next week', 'Emma's dance')"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                color: theme.palette.text.secondary,
                display: { xs: 'none', sm: 'block' },
                mr: 1,
              }}
            >
              ESC to close
            </Typography>
            <IconButton onClick={handleSearchClose} size="small">
              <CloseIcon />
            </IconButton>
          </Paper>
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: theme.palette.text.secondary,
              textAlign: 'center',
            }}
          >
            Press Enter to search or ESC to close
          </Typography>
        </Box>
      </Fade>

      {/* Mobile Bottom Navigation Bar */}
      {isSmallScreen && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            pb: 'env(safe-area-inset-bottom)',
          }}
          elevation={8}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={handleBottomNavChange}
            showLabels
            sx={{
              height: 56,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 0',
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                '&.Mui-selected': {
                  fontSize: '0.7rem',
                },
              },
            }}
          >
            <BottomNavigationAction
              label={t('nav.dashboard')}
              value="/dashboard"
              icon={<DashboardIcon />}
            />
            <BottomNavigationAction
              label={t('nav.children')}
              value="/children"
              icon={<ChildCareIcon />}
            />
            <BottomNavigationAction
              label={t('nav.activities')}
              value="/activities"
              icon={<SportsIcon />}
            />
            <BottomNavigationAction
              label={t('nav.calendar')}
              value="/calendar"
              icon={<CalendarIcon />}
            />
            <BottomNavigationAction
              label={t('nav.chores')}
              value="/chores"
              icon={<AssignmentIcon />}
            />
            <BottomNavigationAction
              label={t('nav.tasks')}
              value="/tasks"
              icon={<CheckCircleIcon />}
            />
            <BottomNavigationAction
              label={t('nav.grocery')}
              value="/grocery"
              icon={<ShoppingCartIcon />}
            />
          </BottomNavigation>
        </Paper>
      )}

      {/* Floating Action Button for Quick Add */}
      {isSmallScreen && (
        <Fab
          color="primary"
          aria-label="quick add activity"
          onClick={handleQuickAdd}
          sx={{
            position: 'fixed',
            bottom: { xs: 72, sm: 80 }, // Above bottom nav
            right: 16,
            zIndex: theme.zIndex.speedDial,
            boxShadow: theme.shadows[6],
            '&:hover': {
              transform: 'scale(1.1)',
            },
            transition: 'transform 0.2s ease-in-out',
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default AppLayout; 